import {
  Alert,
  Button,
  Card,
  Group,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageTitle, QueryState } from '../../ui/components';
import { useWorkplace } from '../access';
import { unwrap, useDb } from '../supabase';
import { AdminOnly } from './AdminOnly';

/** A1 Companies (Admin): create, rename, RISQ module on/off. Admin writes reference data directly. */
export function AdminCompaniesPage() {
  const { t } = useTranslation('dashboard');
  const db = useDb();
  const client = useQueryClient();
  const { access } = useWorkplace();
  const companies = useQuery({
    queryKey: ['admin', 'companies'],
    queryFn: async () => ({
      companies: unwrap(
        await db.from('companies').select('id, name, short_name, archived_at').order('name'),
      ),
      modules: unwrap(await db.from('company_modules').select('id, company_id, module, enabled')),
    }),
    enabled: access.isAdmin,
  });
  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const refresh = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: ['admin', 'companies'] }),
      client.invalidateQueries({ queryKey: ['access'] }),
    ]);
  const create = useMutation({
    mutationFn: async () => {
      const [company] = unwrap(
        await db
          .from('companies')
          .insert({
            name: name.trim(),
            short_name: shortName.trim() === '' ? null : shortName.trim(),
          })
          .select('id'),
      );
      if (company !== undefined) {
        unwrap(
          await db
            .from('company_modules')
            .insert({ company_id: company.id, module: 'risq' })
            .select('id'),
        );
      }
    },
    onSuccess: async () => {
      setName('');
      setShortName('');
      await refresh();
    },
  });
  const toggle = useMutation({
    mutationFn: async ({
      companyId,
      moduleId,
      enabled,
    }: {
      companyId: string;
      moduleId: string | null;
      enabled: boolean;
    }) => {
      if (moduleId === null) {
        unwrap(
          await db
            .from('company_modules')
            .insert({ company_id: companyId, module: 'risq', enabled })
            .select('id'),
        );
      } else {
        unwrap(
          await db.from('company_modules').update({ enabled }).eq('id', moduleId).select('id'),
        );
      }
    },
    onSuccess: refresh,
  });
  const error = create.error ?? toggle.error;
  return (
    <AdminOnly>
      <PageTitle title={t('admin.companiesTitle')} />
      <Stack gap="lg">
        <Card withBorder>
          <Stack>
            <Title order={2} size="h3">
              {t('admin.newCompany')}
            </Title>
            <Group align="flex-end">
              <TextInput
                label={t('admin.companyName')}
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
              />
              <TextInput
                label={t('admin.shortName')}
                value={shortName}
                onChange={(e) => setShortName(e.currentTarget.value)}
              />
              <Button
                disabled={name.trim() === ''}
                loading={create.isPending}
                onClick={() => create.mutate()}
              >
                {t('admin.create')}
              </Button>
            </Group>
          </Stack>
        </Card>
        {error === null ? null : (
          <Alert color="red" role="alert">
            {t('common.saveFailed', { message: error.message })}
          </Alert>
        )}
        <QueryState loading={companies.isPending} error={companies.error}>
          {() => (
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('admin.companyName')}</Table.Th>
                  <Table.Th>{t('admin.shortName')}</Table.Th>
                  <Table.Th>{t('admin.risqModule')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(companies.data?.companies ?? []).map((c) => {
                  const module = companies.data?.modules.find(
                    (m) => m.company_id === c.id && m.module === 'risq',
                  );
                  return (
                    <Table.Tr key={c.id}>
                      <Table.Td>
                        <Text fw={600}>{c.name}</Text>
                      </Table.Td>
                      <Table.Td>{c.short_name ?? ''}</Table.Td>
                      <Table.Td>
                        <Switch
                          label={module?.enabled === true ? t('admin.on') : t('admin.off')}
                          checked={module?.enabled === true}
                          onChange={(e) =>
                            toggle.mutate({
                              companyId: c.id,
                              moduleId: module?.id ?? null,
                              enabled: e.currentTarget.checked,
                            })
                          }
                        />
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          )}
        </QueryState>
      </Stack>
    </AdminOnly>
  );
}
